import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Grid } from '@mui/material';
import { addMpiWorkItem } from '../../../../../../actions/apiMpiWorkItems';
import { addWorkItemPartsBulk } from '../../../../../../actions/apiWorkItemParts';
import { presetPartsToItems } from '../../../../../../components/Parts/partsUtils';
import { toNumber } from '../utils/toNumber';
import _ from 'lodash';

const PresetSelector = ({
  open, onClose, selectedPresets, setSelectedPresets, selectedCategory, setSelectedCategory,
  sections, active_vehicle, vehicle_id, default_labor_rate, form, refreshVehicle, setError
}) => {
  const dispatch = useDispatch();
  const work_item_presets = useSelector(state => state.settings_refresh?.work_item_presets || []);
  const catalog_parts = useSelector(state => state.settings_refresh?.catalog_parts || []);
  const currentUser = useSelector(state => state.current_user || {});
  const installation = useSelector(state => state.settings_dealerships?.installation || {});
  const uid = parseInt(currentUser.user, 10) || null;

  const presetData = useMemo(() => {
    const filteredPresets = _.sortBy(work_item_presets, 'name')
      .filter((preset) =>
        selectedCategory === 'all' ? true : preset.category === selectedCategory
      )
      .map((preset) => ({
        id: preset.id,
        name: preset.name,
        category: preset.category,
        parts: preset.parts_price || 0,
        labor_hours: preset.labor_units || 0,
        labor_rate: preset.labor_price || default_labor_rate,
        estimate_total: preset.estimate_total || 0,
        estimate_type: preset.estimate_type || 'blank_parts_and_labor',
        parts_editable: preset.parts_price_editable || false,
        labor_hours_editable: preset.labor_units_editable || false,
        labor_rate_editable: preset.labor_price_editable || false,
        estimate_total_editable: preset.estimate_total_editable || false,
        description: preset.description || ''
      }));
    return filteredPresets;
  }, [work_item_presets, selectedCategory, default_labor_rate]);

  const handleCreateMultiple = (sectionId) => {
    if (Object.keys(selectedPresets).length === 0) {
      onClose();
      return;
    }

    const selectedRows = Object.keys(selectedPresets)
      .filter((index) => selectedPresets[index])
      .map((index) => presetData[parseInt(index)]);

    const vid = active_vehicle.id || vehicle_id;
    const createOne = (preset) => {
      const labor_total = toNumber(preset.labor_hours) * toNumber(preset.labor_rate);
      const estimate_total = toNumber(preset.parts) + labor_total;
      const data = {
        vehicle_id: vid,
        name: preset.name,
        description: preset.description || 'New Custom Item',
        layout_type: 'default',
        labor_rate: preset.labor_rate || default_labor_rate,
        quantity: 1,
        labor_hours: preset.labor_hours,
        parts: preset.parts,
        order: (active_vehicle.mpi_work_items?.length || 0) + 1,
        show_mpi: true,
        active: true,
        deleted: false,
        dealership: installation.subdomain,
        estimate_total: estimate_total,
        work_item_preset_id: preset.id,
        category: preset.category,
        estimate_type: preset.estimate_type,
        form,
        section: sectionId,
        ro_status: 'pending'
      };
      return dispatch(addMpiWorkItem(data)).then(newId => ({ preset, newId }));
    };

    Promise.all(selectedRows.map(createOne))
      .then((created) => {
        const items = [];
        created.forEach(({ preset, newId }) => {
          if (!newId) return;
          const fullPreset = work_item_presets.find(p => String(p.id) === String(preset.id));
          const basePayload = {
            vehicle_id: vid, work_item_source: 'new_work_items', mpi_work_item_id: newId, dealership: installation.subdomain,
          };
          items.push(...presetPartsToItems(fullPreset, catalog_parts, basePayload, uid));
        });
        if (items.length) return dispatch(addWorkItemPartsBulk(items, vid));
        return null;
      })
      .then(refreshVehicle)
      .catch(() => setError('Failed to add multiple work items'));
    setSelectedPresets({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Select Work Items</DialogTitle>
      <DialogContent sx={{ padding: 1 }}>
        <Box sx={{ mb: 2, display: 'flex', gap: '1rem' }}>
          <Button
            variant={selectedCategory === 'all' ? 'contained' : 'outlined'}
            onClick={() => setSelectedCategory('all')}
          >
            All
          </Button>
          <Button
            variant={selectedCategory === 'mechanical' ? 'contained' : 'outlined'}
            onClick={() => setSelectedCategory('mechanical')}
          >
            Mechanical
          </Button>
          <Button
            variant={selectedCategory === 'cosmetic' ? 'contained' : 'outlined'}
            onClick={() => setSelectedCategory('cosmetic')}
          >
            Cosmetic
          </Button>
        </Box>
        <Grid
          container
          spacing={1}
          sx={{
            maxHeight: '60vh',
            overflowY: 'auto',
            justifyContent: 'space-between',
          }}
        >
          {presetData.map((item, index) => (
            <Grid key={index} size={3}>
              <Box
                sx={{
                  padding: '4px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  backgroundColor: selectedPresets[index]
                    ? '#e3f2fd'
                    : active_vehicle.mpi_work_items.some(
                        (mpi) => mpi.name === item.name && mpi.form === form
                      )
                    ? '#e0e0e0'
                    : 'white',
                  textAlign: 'center',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setSelectedPresets((prev) => {
                    const newSelection = { ...prev };
                    newSelection[index] = !newSelection[index];
                    return newSelection;
                  });
                }}
              >
                {item.name}
              </Box>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error">
          Cancel
        </Button>
        {sections.map(section => (
          <Button
            key={section.id}
            onClick={() => handleCreateMultiple(section.id)}
            color="success"
            disabled={Object.keys(selectedPresets).length === 0}
          >
            Create in {section.label}
          </Button>
        ))}
      </DialogActions>
    </Dialog>
  );
};

export default PresetSelector;